import { DesignMaterialPath } from "common/materials/library/designMaterialPath";
import { DesignMaterialTemplateColorLibs } from "common/materials/library/designMaterialTemplateColorLibs";
import { DMC } from "common/materials/library/DMC";
import { TemplateIdToName } from "common/materials/library/templateIdToName";
import type { MaterialSrc } from "common/materials/materialSrc";

export type DesignMaterialPathNode = { [key: string]: DesignMaterialPathNode } | MaterialSrc;

export const dmPathWithColorRe = /(?<template>\w+)\.C\.(?<colorlib>\w+)\.(?<color>\w+)/;
export const dmPathWithColorLibRe = /^(?<template>\w+)\.C$/;
export const dmPathWithLayerRe = /(?<template>\w+)\.L\.(?<layerconfiglib>\w+)\.(?<layerconfig>\w+)/;
export const prefixedDmPathWithColorRe = new RegExp("DesignMaterialPath." + dmPathWithColorRe.source);

export function getColor(colorlib: string, color: string): number | undefined {
  const dmcRecord = DMC as Record<string, Record<string, number>>;
  if (!dmcRecord.hasOwnProperty(colorlib) || !dmcRecord[colorlib].hasOwnProperty(color)) {
    return undefined;
  }
  return dmcRecord[colorlib][color];
}

export function colorIdFromColorPath(colorPath: string): number | undefined {
  const sepIndex = colorPath.indexOf(".");
  if (sepIndex < 0) {
    return undefined;
  }
  return getColor(colorPath.substring(0, sepIndex), colorPath.substring(sepIndex + 1));
}

/**
 * Returns the id used for a color library or layer config library in paths generated from a specific material template.
 * @param libraryFilename Name of the library file either with or without file extension, not including directory.
 */
export function libraryPathIdFromFilename(libraryFilename: string): string {
  if (libraryFilename.match(/\.[cl]lib\.json$/)) {
    libraryFilename = libraryFilename.substr(0, libraryFilename.length - ".clib.json".length);
  }
  if (libraryFilename.match(/.json$/)) {
    libraryFilename = libraryFilename.substr(0, libraryFilename.length - ".json".length);
  }
  return libraryFilename;
}

export function colorLibNameFromPath(materialPath: string): string | undefined {
  const match = materialPath.match(dmPathWithColorRe);
  return match?.groups?.colorlib;
}

export function templateDefaultColorId(templateId: number): number | undefined {
  const templateIdRecord = TemplateIdToName as Record<string, string>;
  const templateColorLibs = DesignMaterialTemplateColorLibs as Record<string, string[]>;
  const dmcRecord = DMC as Record<string, Record<string, number>>;
  const templateName = templateIdRecord[templateId.toString()];
  if (!templateColorLibs.hasOwnProperty(templateName)) {
    return undefined;
  }
  const colorLibIds: string[] = templateColorLibs[templateName];
  const colorIds: number[] = Object.values(dmcRecord[colorLibIds[0]]);
  return colorIds[0];
}

export function templateHasColorLibs(templateId: number | undefined): boolean {
  if (templateId === undefined) {
    return false;
  }
  const templateIdRecord = TemplateIdToName as Record<string, string>;
  const templateColorLibs = DesignMaterialTemplateColorLibs as Record<string, string[]>;
  const templateName = templateIdRecord[templateId.toString()];
  if (!templateColorLibs.hasOwnProperty(templateName)) {
    return false;
  }
  const colorLibIds: string[] = templateColorLibs[templateName];
  return colorLibIds.length > 0;
}

export function addColorPathStringsToList(list: string[], colorLibIds: string[], pathPrefix: string) {
  const dmcRecord = DMC as Record<string, Record<string, number>>;
  for (const colorLibId of colorLibIds) {
    const libPathId = libraryPathIdFromFilename(colorLibId);
    for (const colorName of Object.keys(dmcRecord[colorLibId])) {
      const path = pathPrefix + libPathId + "." + colorName;
      list.push(path);
    }
  }
}

/** Total color count across the given libraries, without allocating a path string per color. */
export function colorCountForLibraries(colorLibIds: string[]): number {
  const dmcRecord = DMC as Record<string, Record<string, number>>;
  let count = 0;
  for (const colorLibId of colorLibIds) {
    count += Object.keys(dmcRecord[colorLibId]).length;
  }
  return count;
}

export function countMaterialSrcsForTemplate(templateId: number): number {
  const templateIdRecord = TemplateIdToName as Record<number, string>;
  const templateColorLibs = DesignMaterialTemplateColorLibs as Record<string, string[]>;
  const dmcRecord = DMC as Record<string, Record<string, number>>;
  let count = 0;
  const templateName = templateIdRecord[templateId];
  if (DesignMaterialPath[templateName].hasOwnProperty("Default")) {
    ++count;
  }
  if (DesignMaterialPath[templateName].hasOwnProperty("L")) {
    const layerConfigLibs: DesignMaterialPathNode = DesignMaterialPath[templateName]["L"];
    for (const libNode of Object.values(layerConfigLibs)) {
      count += Object.keys(libNode).length;
    }
  }
  if (DesignMaterialPath[templateName].hasOwnProperty("C")) {
    const colorLibNames: string[] = templateColorLibs[templateName];
    for (const colorLibName of colorLibNames) {
      count += Object.keys(dmcRecord[colorLibName]).length;
    }
  }
  return count;
}

/**
 * Get a list of design material variants based on a template.
 * @maxCount maximum number to add to the list.
 * @param skipCount Skip first N material variants.
 */
export function materialSrcsForTemplate(templateId: number, maxCount?: number, skipCount?: number): MaterialSrc[] {
  const templateIdRecord = TemplateIdToName as Record<number, string>;
  const templateColorLibs = DesignMaterialTemplateColorLibs as Record<string, string[]>;
  const dmcRecord = DMC as Record<string, Record<string, number>>;
  const materialIds: MaterialSrc[] = [];
  const templateName = templateIdRecord[templateId];

  function pushId(materialSrc: MaterialSrc) {
    if (skipCount === 0 || skipCount === undefined) {
      materialIds.push(materialSrc);
    } else {
      --skipCount;
    }
  }

  if (DesignMaterialPath[templateName].hasOwnProperty("Default")) {
    pushId({ ...DesignMaterialPath[templateName]["Default"] });
  }
  if (DesignMaterialPath[templateName].hasOwnProperty("L")) {
    const layerConfigLibs: DesignMaterialPathNode = DesignMaterialPath[templateName]["L"];
    for (const layerConfigLib of Object.values(layerConfigLibs)) {
      const layerConfigs: MaterialSrc[] = Object.values(layerConfigLib);
      for (const layerConfig of layerConfigs) {
        if (maxCount !== undefined && materialIds.length >= maxCount) {
          return materialIds;
        }
        pushId({ ...layerConfig });
      }
    }
  }
  if (DesignMaterialPath[templateName].hasOwnProperty("C")) {
    const colorLibNames: string[] = templateColorLibs[templateName];
    for (const colorLibName of colorLibNames) {
      if (maxCount !== undefined && materialIds.length >= maxCount) {
        return materialIds;
      }
      for (const colorId of Object.values(dmcRecord[colorLibName])) {
        if (maxCount !== undefined && materialIds.length >= maxCount) {
          return materialIds;
        }
        pushId({ ...DesignMaterialPath[templateName]["C"], colorId: colorId });
      }
    }
  }
  return materialIds;
}
